import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { Toast, ToastType } from '../../shared/models/toast.model';
import { ToastService } from '../../shared/services/toast.service';

@Component({
  selector: 'dr-customer-offers-ui-toast',
  templateUrl: './toast.component.html',
  styleUrls: ['./toast.component.scss'],
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToastComponent implements OnInit {
  toastsSignal = signal<Toast[]>([]);
  expandedToastsSignal = signal(new Set<string>());

  private toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  private toastRemainingTime = new Map<string, number>();
  private toastHoverStartTime = new Map<string, number>();
  private toastLongDescription = new Map<string, boolean>();

  private iconMap: Record<ToastType, string> = {
    success: 'check_circle',
    error: 'warning',
	warning: 'warning',
    info: 'info'
  };

  constructor(
    private toastService: ToastService,
    private destroyRef: DestroyRef
  ) {
    this.destroyRef.onDestroy(() => {
      this.toastTimeouts.forEach(clearTimeout);
      this.toastTimeouts.clear();
    });
  }

  ngOnInit(): void {
    this.toastService.toasts$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((toast) => this.addToast(toast));
    this.toastService.close$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => this.removeToast(id));
  }
  private addToast(toast: Toast): void {
    this.toastsSignal.update((t) => [...t, toast]);
    this.toastLongDescription.set(toast.id, this.computeIsLongDescription(toast.description));
    if (toast.timeout && toast.timeout > 0) {
      this.startToastTimeout(toast.id, toast.timeout);
    }
  }

  private startToastTimeout(id: string, timeout: number): void {
    this.clearToastTimeout(id);
    const timeoutId = setTimeout(() => {
      this.removeToast(id);
    }, timeout);

    this.toastTimeouts.set(id, timeoutId);
    this.toastRemainingTime.set(id, timeout);
  }

  removeToast(id: string): void {
    this.toastsSignal.update((t) => t.filter((x) => x.id !== id));

    this.expandedToastsSignal.update((set) => {
      const next = new Set(set);
      next.delete(id);
      return next;
    });

    this.clearToastTimeout(id);
    this.toastLongDescription.delete(id);
  }

  private clearToastTimeout(id: string): void {
    const timeoutId = this.toastTimeouts.get(id);

    if (timeoutId) {
      clearTimeout(timeoutId);
      this.toastTimeouts.delete(id);
    }

    this.toastRemainingTime.delete(id);
    this.toastHoverStartTime.delete(id);
  }

  onToastMouseEnter(id: string): void {
    const timeoutId = this.toastTimeouts.get(id);

    if (timeoutId) {
      clearTimeout(timeoutId);
      this.toastTimeouts.delete(id);
      this.toastHoverStartTime.set(id, Date.now());
    }
  }

  onToastMouseLeave(id: string): void {
    const hoverStartTime = this.toastHoverStartTime.get(id);

    if (!hoverStartTime) return;

    const hoverDuration = Date.now() - hoverStartTime;
    const remainingTime = this.toastRemainingTime.get(id) || 0;
    const newRemainingTime = Math.max(remainingTime - hoverDuration, 1000);

    this.toastHoverStartTime.delete(id);
    this.toastRemainingTime.set(id, newRemainingTime);

    this.startToastTimeout(id, newRemainingTime);
  }

  getIcon(type: ToastType): string {
    return this.iconMap[type] ?? 'info';
  }

  toggleExpand(id: string): void {
    this.expandedToastsSignal.update((set) => {
      const next = new Set(set);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  isExpanded(id: string): boolean {
    return this.expandedToastsSignal().has(id);
  }

  isLongDescription(id: string): boolean {
    return this.toastLongDescription.get(id) ?? false;
  }

  private computeIsLongDescription(description: string): boolean {
    const lineCount = (description.match(/\n/g) || []).length + 1;
    return lineCount > 2 || description.length > 100;
  }
  trackById(_: number, toast: Toast): string {
    return toast.id;
  }
}
